import React from 'react';
import { Box, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Bank, Flag, Gauge, Scales, TrendDown, TrendUp, WarningCircle } from 'phosphor-react';

import { GlassCard, GlassPageBg } from '../../../../components/glass';
import { IAgingFaixa, useFinanceiroDashboard } from '../../../../hooks/useFinanceiro';
import { AnimatedBar } from '../components/AnimatedBar';
import { FinanceiroPageHeader } from '../components/FinanceiroPageHeader';
import { brl, fmtData } from '../components/format';
import { InfoPopover } from '../components/InfoPopover';
import { KpiCard } from '../components/KpiCard';
import { useFinanceiroFilters } from '../components/useFinanceiroFilters';

const FAIXA_LABEL: Record<string, string> = {
  A_VENCER: 'A vencer',
  D_1_30: '1–30 dias',
  D_31_60: '31–60 dias',
  D_61_90: '61–90 dias',
  D_90_MAIS: '+90 dias',
};

const AgingBar = ({ faixa, max }: { faixa: IAgingFaixa; max: number }) => {
  const pct = max > 0 ? Math.max(4, (faixa.valor / max) * 100) : 0;
  const vencido = faixa.faixa !== 'A_VENCER';
  return (
    <Box>
      <Flex justify="space-between" fontSize="xs" color="#71717A" mb={1}>
        <Text>{FAIXA_LABEL[faixa.faixa] ?? faixa.faixa}</Text>
        <Text fontWeight={600} color="#0F172A">
          {brl(faixa.valor)}
        </Text>
      </Flex>
      <AnimatedBar pct={pct} cor={vencido ? '#D97706' : '#1B88F5'} h="8px" />
    </Box>
  );
};

export const FinanceiroDashboardPage = () => {
  const f = useFinanceiroFilters();
  const { data, isError } = useFinanceiroDashboard({ base: 'vencimento', ...f.apiParams });

  const rec = data?.receber;
  const pag = data?.pagar;
  const agingMax = Math.max(1, ...(data?.aging_receber ?? []).map(af => af.valor));

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <FinanceiroPageHeader
          eyebrow="Visão geral"
          titulo="Dashboard"
          subtitulo="Recebíveis, pagáveis, inadimplência e resultado num só lugar"
          icon={<Gauge size={24} weight="fill" />}
          cor="#1B88F5"
          info={
            <Text>
              Visão consolidada do escritório no período e franquia escolhidos. Os 4 cartões do
              topo: <b>A Receber</b> e <b>A Pagar</b> em aberto, <b>Saldo Previsto</b> (a diferença)
              e <b>Inadimplência</b> (vencido). Abaixo: aging, top inadimplentes e resultado por
              categoria.
            </Text>
          }
        />

        {isError && (
          <GlassCard intensity="medium" p={6} mb={5} tint="#DC2626">
            <Text color="#71717A">Não foi possível carregar os indicadores.</Text>
          </GlassCard>
        )}

        {/* KPIs */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
          <KpiCard
            label="A Receber"
            valor={rec?.em_aberto.valor ?? 0}
            qtd={rec?.em_aberto.qtd}
            color="#16A34A"
            icon={<TrendUp size={20} weight="duotone" />}
            index={0}
            info={
              <InfoPopover title="A Receber">
                Saldo em aberto a receber (<i>nao_pago</i>) de títulos com saldo que ainda{' '}
                <b>não venceram</b>. Base: vencimento.
              </InfoPopover>
            }
          />
          <KpiCard
            label="A Pagar"
            valor={pag?.em_aberto.valor ?? 0}
            qtd={pag?.em_aberto.qtd}
            color="#DC2626"
            icon={<TrendDown size={20} weight="duotone" />}
            index={1}
            info={
              <InfoPopover title="A Pagar">
                Saldo em aberto a pagar (<i>nao_pago</i>) de despesas com saldo ainda não vencidas.
                Base: vencimento.
              </InfoPopover>
            }
          />
          <KpiCard
            label="Saldo Previsto"
            valor={data?.saldo_previsto ?? 0}
            color="#1B88F5"
            icon={<Scales size={20} weight="duotone" />}
            index={2}
            info={
              <InfoPopover title="Saldo Previsto">
                <b>A Receber − A Pagar</b> em aberto no período (aproximação gerencial do que deve
                sobrar/faltar).
              </InfoPopover>
            }
          />
          <KpiCard
            label="Inadimplência"
            valor={rec?.vencido.valor ?? 0}
            qtd={rec?.vencido.qtd}
            color="#D97706"
            icon={<WarningCircle size={20} weight="duotone" />}
            index={3}
            info={
              <InfoPopover title="Inadimplência">
                Saldo <b>vencido</b> a receber — recebíveis vencidos <b>até o fim do período</b> e
                ainda não pagos. O início do período não corta (o atrasado antigo não some); no mês
                corrente equivale ao saldo de hoje.
              </InfoPopover>
            }
          />
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
          {/* Aging */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <GlassCard intensity="strong" p={6} h="full">
              <Flex align="center" gap={2} mb={4}>
                <Bank size={18} color="#1B88F5" weight="duotone" />
                <Text fontWeight={700} color="#0F172A">
                  Aging de recebíveis
                </Text>
              </Flex>
              <Flex direction="column" gap={3}>
                {(data?.aging_receber ?? []).map(af => (
                  <AgingBar key={af.faixa} faixa={af} max={agingMax} />
                ))}
                {(data?.aging_receber?.length ?? 0) === 0 && (
                  <Text fontSize="sm" color="#94A3B8">
                    Sem recebíveis em aberto.
                  </Text>
                )}
              </Flex>
            </GlassCard>
          </motion.div>

          {/* Top inadimplentes */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.05 }}
          >
            <GlassCard intensity="medium" p={6} h="full">
              <Flex align="center" gap={2} mb={4}>
                <Flag size={18} color="#D97706" weight="duotone" />
                <Text fontWeight={700} color="#0F172A">
                  Top inadimplentes
                </Text>
              </Flex>
              <Flex direction="column" gap={3}>
                {(data?.top_inadimplentes ?? []).map(it => (
                  <Flex
                    key={`inad-${it.pessoa_nome}-${it.data_vencimento}-${it.nao_pago}`}
                    justify="space-between"
                    align="center"
                    gap={2}
                  >
                    <Box minW={0}>
                      <Text fontSize="sm" fontWeight={600} color="#0F172A" noOfLines={1}>
                        {it.pessoa_nome || '—'}
                      </Text>
                      <Text fontSize="xs" color="#94A3B8">
                        venc. {fmtData(it.data_vencimento)}
                      </Text>
                    </Box>
                    <Text fontSize="sm" fontWeight={700} color="#DC2626" whiteSpace="nowrap">
                      {brl(it.nao_pago)}
                    </Text>
                  </Flex>
                ))}
                {(data?.top_inadimplentes?.length ?? 0) === 0 && (
                  <Text fontSize="sm" color="#94A3B8">
                    Sem inadimplência no período.
                  </Text>
                )}
              </Flex>
            </GlassCard>
          </motion.div>
        </SimpleGrid>

        {/* DRE por categoria */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1 }}
        >
          <GlassCard intensity="medium" p={6} mt={5}>
            <Text fontWeight={700} color="#0F172A" mb={4}>
              Resultado por categoria
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              {(['RECEITA', 'DESPESA'] as const).map(tp => (
                <Box key={tp}>
                  <Text
                    fontSize="xs"
                    fontWeight={700}
                    color={tp === 'RECEITA' ? '#16A34A' : '#DC2626'}
                    mb={2}
                    textTransform="uppercase"
                  >
                    {tp === 'RECEITA' ? 'Receitas' : 'Despesas'}
                  </Text>
                  <Flex direction="column" gap={2}>
                    {(data?.dre ?? [])
                      .filter(l => l.tipo === tp)
                      .slice(0, 6)
                      .map(l => (
                        <Flex key={`${tp}-${l.categoria}`} justify="space-between" fontSize="sm">
                          <Text color="#71717A" noOfLines={1}>
                            {l.categoria}
                          </Text>
                          <Text color="#0F172A" fontWeight={600} whiteSpace="nowrap">
                            {brl(l.total)}
                          </Text>
                        </Flex>
                      ))}
                    {(data?.dre ?? []).filter(l => l.tipo === tp).length === 0 && (
                      <Text fontSize="sm" color="#94A3B8">
                        Sem dados.
                      </Text>
                    )}
                  </Flex>
                </Box>
              ))}
            </SimpleGrid>
          </GlassCard>
        </motion.div>
      </Box>
    </GlassPageBg>
  );
};
